import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';

import BoostModalTitle from '@features/boostMyJobs/components/BoostModalTitle/BoostModalTitle';
import BoostNote from '@features/boostMyJobs/components/BoostNote/BoostNote';
import SocialBoostModalContent from '@features/boostMyJobs/components/SocialBoostModalContent/SocialBoostModalContent';
import { useBoostSocial } from '@features/boostMyJobs/hooks/useBoostSocial';
import { useEnterpriseBoostLocation } from '@features/boostMyJobs/hooks/useEnterpriseBoostLocation';
import { useEnterpriseBoostSocial } from '@features/boostMyJobs/hooks/useEnterpriseBoostSocial';
import { authContext } from '@staffedupusa/shared-components';
import ChargeforBoost from '@features/enterprise/components/ChargeforBoost';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { Close } from '@mui/icons-material';
import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';

type SocialBoostModalProps = {
  isOpen: boolean;
  selectedCompanyId?: string | number;
  companySelfPaid?: boolean;
  handleClose: () => void;
};

const SocialBoostModal: FC<SocialBoostModalProps> = ({
  isOpen,
  selectedCompanyId,
  companySelfPaid,
  handleClose,
}) => {
  const { authData } = useContext(authContext);
  const isEnterprise = authData?.user?.enterprise;
  const isCompany = authData?.user?.company;
  const isCompanyPaid = authData?.user?.company?.is_company_paid;
  const [errorText, setErrorText] = useState('');
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [hasSelfPaymentMethod, setHasSelfPaymentMethod] = useState(false);

  const { boostSocial, isBoostSocialLoading } = useBoostSocial({
    setErrorText,
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { boostEnterpriseSocial, isBoostEnterpriseSocialLoading } =
    useEnterpriseBoostSocial({
      setErrorText,
    });

  const { paymentInfo } = useGetPaymantDetails({
    companyId: selectedCompanyId,
  });
  const { paymentInfo: enterprisePaymentInfo } = useGetPaymantDetails();

  const handleBoost = () => {
    const options = {
      onSuccess: (data: { status: number; message: any }) => {
        if (data.status === 200) {
          toast.success('Boost Successful');
          handleClose();
        } else {
          setErrorText(data?.message || 'Error occurred while boosting');
        }
      },
    };

    if (isCompany) {
      if ((hasSelfPaymentMethod && isCompanyPaid) || !isCompanyPaid) {
        boostSocial({ company_id: selectedCompanyId || undefined }, options);
      } else {
        setShowPaymentModal(true);
      }
    } else if (
      (isEnterprise && !companySelfPaid && hasSelfPaymentMethod) ||
      (isEnterprise && companySelfPaid && hasPaymentMethod)
    ) {
      boostEnterpriseSocial({ company_id: selectedCompanyId }, options);
    } else if(isEnterprise && companySelfPaid && !hasPaymentMethod){
      setShowPaymentModal(true);
    }
  };
  useEffect(() => {
    if (
      paymentInfo &&
      paymentInfo?.object &&
      paymentInfo?.object?.data?.length > 0
    ) {
      setHasPaymentMethod(true);
    } else {
      setHasPaymentMethod(false);
    }
  }, [paymentInfo]);
  useEffect(() => {
    if (
      enterprisePaymentInfo &&
      enterprisePaymentInfo?.object &&
      enterprisePaymentInfo?.object?.data?.length > 0
    ) {
      setHasSelfPaymentMethod(true);
    } else {
      setHasSelfPaymentMethod(false);
    }
  }, [enterprisePaymentInfo]);
  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <BoostModalTitle title="StaffedUp Social Boost!" />
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Stack sx={{ marginBottom: 4 }} spacing={4} alignItems="center">
          {errorText && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {errorText}
            </Alert>
          )}
          <SocialBoostModalContent />
          <LoadingButton
            onClick={handleBoost}
            variant="contained"
            size="large"
            loading={isBoostEnterpriseSocialLoading}
            disabled={isBoostSocialLoading}
            sx={{ textTransform: 'uppercase' }}
          >
            Request My Boost now
          </LoadingButton>
          <BoostNote color="text.secondary" />
        </Stack>
      </DialogContent>
      {showPaymentModal && (
        <ChargeforBoost
          handleClose={() => {
            handleClose();
            setShowPaymentModal(false);
          }}
          selectedCompanyId={selectedCompanyId}
          socialBoost={true}
        />
      )}
    </Dialog>
  );
};

export default SocialBoostModal;
